import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import KnotapiJS from "knotapi-js";

import EarnMore from "../../Organisms/Earn More Card";
import CustomStepper from "../../Components/Others/custom-stepper";

import renderReferAFriend from "../../Organisms/Modals/Banking/refer-a-friend";
import renderSelectConnectedAccounts from "../../Organisms/Modals/Bank Account/select-connected-accounts";

import useStakeCheckingStatusAction from "../../CustomHooks/useStakeCheckingStatusAction";

import { useModalContext } from "../../Contexts/ModalContext";

import { useCreateKnotSession } from "../../CustomHooks/Mutations";

import {
  isSVDPending,
  isSVDCardRequested,
  dispatchAPIErrorToast,
  isSVDDenied,
  hasSVDPhysicalCardArrived,
  formatDate,
  isSVDApproved,
  appendISOTime,
  hasSVDVirtualCard,
  hasSVDPhysicalCard,
  isSVDEligible,
  isSVDClosedForStakeSaverNonCompliance,
  isSVDVerificationRequired,
  isSVDPendingVerification,
  isSVDReview,
} from "../../Utils/helpers";
import { nilYearLongMonthDateFormat } from "../../Utils/constants";

import { AddABank, ExpressPaycheck, KnotOffer, ReferAFriend, StakeDebit } from "../../Assets/Images";

export default function EarnMoreCards({
  connectedAccounts = {},
  stakeDebit = {},
  deals = {},
  payrollAccountsData = {},
  payrollProps = {},
  fromWelcome = false,
}) {
  const modalContext = useModalContext();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { show: showIntercom } = useIntercom();

  const IsSVDApproved = useMemo(() => isSVDApproved(stakeDebit?.data), [stakeDebit?.data]);

  const {
    isFetchingPayrollAccounts,
    isGettingPayrollLinkToken,
    refetchingPayrollDataWithDebounce,
    GetPayrollLinkToken,
    PinwheelComponent,
  } = payrollProps;

  const isBankAccountConnected = useMemo(() => {
    return connectedAccounts?.data?.data?.accounts?.length > 0;
  }, [connectedAccounts]);

  const referAFriend = useMemo(() => {
    return deals?.data?.content?.homescreen?.cards?.find((card) => "refer_a_friend" in card)?.refer_a_friend || null;
  }, [deals]);

  const isPayrollConnected = useMemo(() => {
    return payrollAccountsData?.data?.data?.accounts?.length > 0;
  }, [payrollAccountsData]);

  const earnMoreGuideReferAFriend = useMemo(() => {
    return deals?.data?.content?.earn_more_guide?.cards?.[0]?.refer_a_friend || null;
  }, [deals]);

  const HasSVDVirtualcard = useMemo(() => hasSVDVirtualCard(stakeDebit), [stakeDebit]);

  const HasSVDPhysicalCard = useMemo(() => hasSVDPhysicalCard(stakeDebit), [stakeDebit]);

  const stakeCheckingStatusAction = useStakeCheckingStatusAction();

  const { isLoading: isCreatingKnotSession, mutate: CreateKnotSession } = useCreateKnotSession({
    onSuccess: (res) => {
      const knotapi = new KnotapiJS();

      knotapi.open({
        sessionId: res?.data?.data?.session,
        clientId: process.env.REACT_APP_KNOT_CLIENT_ID,
        environment: process.env.REACT_APP_ENV,
        product: "card_switcher",
      });
    },
    onError: () => dispatchAPIErrorToast({ key: "knotSessionToken" }),
  });

  const openKnot = () => CreateKnotSession(stakeDebit?.data?.data?.virtual_card_id ?? stakeDebit?.data?.data?.card_id);

  const stakeCheckingCardProps = useMemo(
    () => {
      const IsSVDEligible = isSVDEligible(stakeDebit?.data);
      const IsSVDCardRequested = isSVDCardRequested(stakeDebit?.data);
      const physicalCardArrivesByDate = new Date(
        IsSVDCardRequested ? appendISOTime(stakeDebit?.data?.data?.physical_card_arrives_by) : 0,
      );

      if (IsSVDCardRequested) {
        return {
          title: stakeDebit?.data?.data?.home_card_header,
          description: stakeDebit?.data?.data?.home_card_body ?? "Call (833)-333-0417 to activate your card.",
          NestComponent: () => (
            <CustomStepper
              steps={[
                { label: "Approved", StepComponent: () => <div className="step-icon"></div> },
                {
                  label: `Arrives by ${formatDate(physicalCardArrivesByDate, nilYearLongMonthDateFormat)}`,
                  StepComponent: () => <div className="step-icon"></div>,
                },
                { label: "Call to activate your card", StepComponent: () => <div className="step-icon"></div> },
              ]}
              activeStep={!hasSVDPhysicalCardArrived(physicalCardArrivesByDate) ? 1 : 2}
              orientation="vertical"
              alternativeLabel={false}
            />
          ),
        };
      } else if (isSVDDenied(stakeDebit?.data)) {
        return {
          description: "Your Stake Checking application was rejected.",
          NestComponent: () => (
            <button className="button" style={{ width: "fit-content" }}>
              Contact Support
            </button>
          ),
          onCardButtonClick: showIntercom,
        };
      } else if (isSVDPendingVerification(stakeDebit?.data)) {
        return {
          description: "Your application requires additional verification.",
          cardButtonText: "Verify",
          onCardButtonClick: () => {
            if (isSVDVerificationRequired(stakeDebit?.data)) {
              navigate("/verify-stake-checking", { state: { previousPathname: pathname } });
            } else {
              stakeCheckingStatusAction(stakeDebit?.data);
            }
          },
        };
      } else if (isSVDPending(stakeDebit?.data)) {
        return {
          description: "Your application is pending.",
          cardButtonText: "View",
          onCardButtonClick: () => stakeCheckingStatusAction(stakeDebit?.data),
        };
      } else if (isSVDReview(stakeDebit?.data)) {
        return {
          description: "Your Stake Checking application is under review.",
          cardButtonText: "View",
          onCardButtonClick: () => stakeCheckingStatusAction(stakeDebit?.data),
        };
      } else if (IsSVDEligible || isSVDClosedForStakeSaverNonCompliance(stakeDebit?.data)) {
        return {
          description: "FDIC-insured with a virtual Stake Visa Debit card. Unlock Cash Back on rent and purchases",
          cardButtonText: "Get started",
          onCardButtonClick: () => stakeCheckingStatusAction(stakeDebit?.data),
        };
      }
      return null;
    }, //eslint-disable-next-line
    [stakeDebit?.data, modalContext, stakeCheckingStatusAction],
  );

  return (
    <>
      {IsSVDApproved && !isPayrollConnected && <PinwheelComponent />}
      <div className="earn-more-cards row gx-sm-5 gx-0">
        {!isBankAccountConnected && (
          <div className="col-sm-6 col-12">
            <EarnMore
              title="Connect a Bank"
              cardButtonText="Connect"
              description="Transfer in funds to pay rent, make purchases, and earn Cash Back!"
              CardIllustrationComponent={AddABank}
              onCardButtonClick={() => renderSelectConnectedAccounts(modalContext, { openPlaidOnMount: true })}
            />
          </div>
        )}

        {!!stakeCheckingCardProps && (
          <div className="col-sm-6 col-12">
            <EarnMore title="Stake Checking" CardIllustrationComponent={StakeDebit} {...stakeCheckingCardProps} />
          </div>
        )}

        {IsSVDApproved && !isPayrollConnected && (
          <div className={`col-sm-6 col-12 ${fromWelcome ? "highlight-pulse-wrapper" : ""}`}>
            <EarnMore
              title="Express Paycheck"
              description="It's your money, get it sooner. Get your paycheck up to two days early."
              cardButtonText="Let's go!"
              CardIllustrationComponent={ExpressPaycheck}
              onCardButtonClick={() => GetPayrollLinkToken()}
              isLoading={isFetchingPayrollAccounts || refetchingPayrollDataWithDebounce || isGettingPayrollLinkToken}
            />
          </div>
        )}

        <div className="col-sm-6 col-12">
          <EarnMore
            title="Refer a friend"
            description={earnMoreGuideReferAFriend?.body ?? "3 friends total at $15 per friend"}
            cardButtonText={earnMoreGuideReferAFriend?.call_to_action ?? "Get $45"}
            CardIllustrationComponent={ReferAFriend}
            onCardButtonClick={renderReferAFriend.bind(this, modalContext, referAFriend)}
          />
        </div>

        {(HasSVDVirtualcard || HasSVDPhysicalCard) && (
          <div className="col-sm-6 col-12">
            <EarnMore
              title={"Add your card to your favorite merchants"}
              description={"Securely and quickly add your card to your favorite merchants"}
              cardButtonText={"Add Card"}
              CardIllustrationComponent={KnotOffer}
              onCardButtonClick={openKnot}
              isLoading={isCreatingKnotSession}
            />
          </div>
        )}
      </div>
    </>
  );
}
